<template>
  <div
    class="glass-card-component"
    :class="[
      `glass-card--${size}`,
      { 'glass-card--hoverable': hoverable },
      { 'glass-card--no-padding': noPadding },
    ]"
  >
    <div v-if="$slots.header || title" class="glass-card__header">
      <slot name="header">
        <div class="glass-card__header-content">
          <h3 v-if="title" class="glass-card__title">{{ title }}</h3>
          <p v-if="subtitle" class="glass-card__subtitle">{{ subtitle }}</p>
        </div>
      </slot>
      <div v-if="$slots.actions" class="glass-card__actions">
        <slot name="actions" />
      </div>
    </div>
    <div class="glass-card__body">
      <slot />
    </div>
    <div v-if="$slots.footer" class="glass-card__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
/**
 * GlassCard - Glassmorphism-style card component
 * @description A modern card with glass effect, backdrop blur, and subtle borders
 * @prop {String} title - Card title
 * @prop {String} subtitle - Card subtitle
 * @prop {String} size - Card size variant (sm, md, lg)
 * @prop {Boolean} hoverable - Enable hover effect
 * @prop {Boolean} noPadding - Remove internal padding
 */
export default {
  name: "GlassCard",
  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg"].includes(value),
    },
    hoverable: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.glass-card-component {
  background: var(--k-bg-glass);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--k-bg-glass-border);
  border-radius: var(--k-radius-lg);
  box-shadow: var(--k-shadow-md);
  overflow: hidden;
  transition: transform var(--k-transition-normal),
    box-shadow var(--k-transition-normal);
}

.glass-card--hoverable:hover {
  transform: translateY(-2px);
  box-shadow: var(--k-shadow-lg);
}

/* Size Variants */
.glass-card--sm {
  border-radius: var(--k-radius-md);
}

.glass-card--sm .glass-card__body {
  padding: 16px;
}

.glass-card--md .glass-card__body {
  padding: 24px;
}

.glass-card--lg .glass-card__body {
  padding: 32px;
}

.glass-card--no-padding .glass-card__body {
  padding: 0;
}

/* Header */
.glass-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 0;
  gap: 16px;
}

.glass-card--sm .glass-card__header {
  padding: 16px 16px 0;
}

.glass-card__header-content {
  flex: 1;
  min-width: 0;
}

.glass-card__title {
  font-size: 17px;
  font-weight: 600;
  color: var(--k-text-primary);
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.glass-card__subtitle {
  font-size: 13px;
  color: var(--k-text-secondary);
  margin: 0;
  line-height: 1.4;
}

.glass-card__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Body */
.glass-card__body {
  /* padding set by size variants */
}

/* Footer */
.glass-card__footer {
  padding: 16px 24px;
  border-top: 1px solid var(--k-border-color);
  background: var(--k-bg-tertiary);
}

.glass-card--sm .glass-card__footer {
  padding: 12px 16px;
}
</style>

